import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { SUBJECTS, subjectStyle } from "@/lib/constants";
import { Plus, Trash2, X, Minus, Trophy, Target } from "lucide-react";
import { toast } from "sonner";

const empty = { title: "", subject: "", target: 10 };
const MONTHS_FR = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];

export default function Goals() {
  const [goals, setGoals] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(true);

  const load = () => api.get("/goals").then(({ data }) => setGoals(data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const now = new Date();
  const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const monthLabel = `${MONTHS_FR[now.getMonth()]} ${now.getFullYear()}`;

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/goals", { ...form, target: Number(form.target), month: currentMonth });
      toast.success("Objectif créé, à toi de jouer !");
      setOpen(false);
      setForm(empty);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const bump = async (goal, delta) => {
    const current = Math.max(0, Math.min(goal.target, goal.current + delta));
    try {
      const { data } = await api.put(`/goals/${goal.id}`, { current });
      if (data.achieved_at && !goal.achieved_at) toast.success("Objectif accompli ! Bravo !");
      setGoals(goals.map((g) => (g.id === goal.id ? data : g)));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/goals/${id}`);
      toast.success("Objectif supprimé");
      setGoals(goals.filter((g) => g.id !== id));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const monthGoals = goals.filter((g) => g.month === currentMonth);
  const achieved = monthGoals.filter((g) => g.achieved_at).length;

  return (
    <div className="space-y-6" data-testid="goals-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Objectifs du mois</h1>
          <p className="text-slate-500 text-sm sm:text-base mt-1 capitalize">{monthLabel} — {achieved}/{monthGoals.length} objectif{monthGoals.length > 1 ? "s" : ""} accompli{achieved > 1 ? "s" : ""}</p>
        </div>
        <button
          onClick={() => setOpen(true)}
          data-testid="add-goal-button"
          className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 transition-colors duration-200"
        >
          <Plus className="h-4 w-4" /> Nouvel objectif
        </button>
      </div>

      {loading ? (
        <div className="animate-pulse grid md:grid-cols-2 lg:grid-cols-3 gap-4">{[...Array(3)].map((_, i) => <div key={i} className="h-44 bg-slate-100 rounded-3xl" />)}</div>
      ) : monthGoals.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-3xl border border-slate-200/80" data-testid="goals-empty">
          <Target className="h-10 w-10 text-indigo-300 mx-auto mb-3" />
          <p className="text-slate-600 font-medium">Aucun objectif pour {monthLabel}.</p>
          <p className="text-sm text-slate-400 mt-1">Ex : « Finir 10 fiches de maths » ou « Travailler 20 jours ce mois-ci ».</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {monthGoals.map((g) => {
            const pct = Math.min(100, Math.round((g.current / g.target) * 100));
            const done = !!g.achieved_at;
            return (
              <div key={g.id} data-testid={`goal-card-${g.id}`} className={`relative bg-white rounded-3xl border p-6 shadow-sm ${done ? "border-emerald-300 bg-emerald-50/40" : "border-slate-200/80"}`}>
                {done && (
                  <span className="absolute -top-3 right-5 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-emerald-500 text-white text-[10px] font-bold uppercase tracking-wider">
                    <Trophy className="h-3 w-3" /> Accompli
                  </span>
                )}
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    {g.subject && (
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${subjectStyle(g.subject)}`}>{g.subject}</span>
                    )}
                    <h3 className="text-base font-semibold text-slate-900 mt-2">{g.title}</h3>
                  </div>
                  <button onClick={() => remove(g.id)} data-testid={`goal-delete-${g.id}`} className="p-1.5 rounded-lg text-slate-300 hover:text-rose-500 hover:bg-rose-50 shrink-0">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                <div className="mt-5">
                  <div className="flex justify-between text-xs font-semibold mb-2">
                    <span className="text-slate-500">{pct} %</span>
                    <span className="text-slate-700" data-testid={`goal-progress-${g.id}`}>{g.current} / {g.target}</span>
                  </div>
                  <div className="h-3 rounded-full bg-slate-100 overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-[width] duration-500 ${done ? "bg-emerald-500" : "bg-gradient-to-r from-indigo-500 to-violet-500"}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
                {!done && (
                  <div className="mt-4 flex items-center gap-2">
                    <button onClick={() => bump(g, -1)} data-testid={`goal-minus-${g.id}`} className="h-9 w-9 rounded-xl bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors duration-200">
                      <Minus className="h-4 w-4 text-slate-600" />
                    </button>
                    <button onClick={() => bump(g, 1)} data-testid={`goal-plus-${g.id}`} className="flex-1 h-9 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold flex items-center justify-center gap-1 transition-colors duration-200">
                      <Plus className="h-4 w-4" /> +1 progrès
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm" data-testid="goal-modal">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-7 animate-fade-in-up">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-semibold text-slate-900">Nouvel objectif</h2>
              <button onClick={() => setOpen(false)} data-testid="goal-modal-close" className="p-2 rounded-lg hover:bg-slate-100"><X className="h-4 w-4" /></button>
            </div>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Objectif</label>
                <input type="text" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="goal-title-input" placeholder="Ex : Finir 10 fiches de révision" className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Matière (optionnel)</label>
                <select value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="goal-subject-select" className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm bg-white">
                  <option value="">Aucune</option>
                  {SUBJECTS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Cible (nombre à atteindre)</label>
                <input type="number" min={1} max={1000} required value={form.target} onChange={(e) => setForm({ ...form, target: e.target.value })} data-testid="goal-target-input" className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm" />
              </div>
              <button type="submit" data-testid="goal-submit-button" className="w-full py-3 rounded-full bg-indigo-600 text-white font-semibold text-sm hover:bg-indigo-700 transition-colors duration-200">
                Créer l'objectif
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
